// src/utils/analytics.ts
/* eslint-disable @typescript-eslint/no-explicit-any */
import posthog from 'posthog-js';

/**
 * Configuration options for PostHog analytics
 */
interface AnalyticsConfig {
  apiKey?: string;
  apiHost?: string;
  enabled?: boolean;
  replaySampleRate?: number;
  debug?: boolean;
}

/**
 * User context for analytics identification
 */
interface UserContext {
  id?: string;
  role?: string;
  hotelId?: string;
  employeeId?: string;
}

/**
 * Singleton class to manage PostHog analytics integration
 * Follows the same patterns established by SentryManager
 */
class AnalyticsManager {
  private static instance: AnalyticsManager;
  private isInitialized = false;
  private isEnabled = false;
  private static readonly OPT_OUT_KEY = 'selfserve:analytics_opt_out';

  public static getInstance(): AnalyticsManager {
    if (!AnalyticsManager.instance) {
      AnalyticsManager.instance = new AnalyticsManager();
    }
    return AnalyticsManager.instance;
  }

  /**
   * Initialize PostHog with production-ready configuration
   */
  public initialize(config: AnalyticsConfig = {}): void {
    if (this.isInitialized) {
      return;
    }

    const enabled = config.enabled ?? import.meta.env.VITE_POSTHOG_ENABLED === 'true';
    const apiKey = config.apiKey || import.meta.env.VITE_POSTHOG_KEY;
    
    // Debug logging (no API key exposure)
    if (import.meta.env.DEV) {
      console.log('🔍 PostHog Debug Info:');
      console.log('- Enabled:', enabled);
      console.log('- API Key:', apiKey ? 'Configured' : 'Missing');
      console.log('- Environment:', import.meta.env.MODE);
    }
    
    // Don't initialize if disabled or no API key is provided
    if (!enabled || !apiKey) {
      console.warn('❌ PostHog analytics disabled - feature flag off or API key missing');
      this.isInitialized = true;
      return;
    }

    const apiHost = config.apiHost || import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';

    try {
      posthog.init(apiKey, {
        api_host: apiHost,
        autocapture: false, // Privacy-first: no autocapture
        capture_pageview: false, // We'll handle page views manually
        debug: config.debug ?? import.meta.env.DEV,
        persistence: 'localStorage',
        
        // Session replay disabled by default for privacy
        // To enable later: set VITE_POSTHOG_REPLAY_SAMPLE_RATE > 0
        
        // Additional privacy settings
        respect_dnt: true,
        opt_out_capturing_by_default: false,
        
        // Advanced options for production
        cross_subdomain_cookie: false,
        secure_cookie: import.meta.env.PROD,
      });

      // Apply persisted opt-out immediately after init
      if (this.isOptedOut()) {
        try {
          posthog.opt_out_capturing();
        } catch (e) {
          if (import.meta.env.DEV) console.warn('PostHog opt-out apply failed:', e);
        }
      }

      this.isEnabled = true;
      this.isInitialized = true;
      if (import.meta.env.DEV) {
        console.log(`✅ PostHog initialized successfully`);
      }
    } catch (error) {
      console.error('❌ Failed to initialize PostHog:', error);
      this.isInitialized = true;
    }
  }

  /**
   * Identify user with privacy-safe context
   */
  public identifyUser(user: UserContext): void {
    if (!this.isEnabled || !user.id) return;

    try {
      // Only send safe, business-relevant properties
      const safeProperties = {
        role: user.role,
        hotelId: user.hotelId,
        employeeId: user.employeeId,
      };

      posthog.identify(user.id, safeProperties);
    } catch (error) {
      console.error('Analytics identify error:', error);
    }
  }

  /**
   * Set additional context properties
   */
  public setContext(properties: Record<string, string | number | boolean>): void {
    if (!this.isEnabled) return;

    try {
      const scrubbedProperties = this.scrubProperties(properties);
      posthog.register(scrubbedProperties);
    } catch (error) {
      console.error('Analytics context error:', error);
    }
  }

  /**
   * Track a custom event with optional properties
   */
  public track(eventName: string, properties?: Record<string, any>): void {
    if (!this.isEnabled) return;

    try {
      const scrubbedProperties = this.scrubProperties(properties);
      posthog.capture(eventName, scrubbedProperties);
    } catch (error) {
      console.error('Analytics track error:', error);
    }
  }

  /**
   * Track feature usage events
   */
  public trackFeatureUsage(feature: string, properties?: Record<string, any>): void {
    this.track('feature_usage', {
      feature,
      ...properties,
    });
  }

  /**
   * Track staff actions for business intelligence
   */
  public trackStaffAction(action: string, properties?: Record<string, any>): void {
    this.track('staff_action', {
      action,
      ...properties,
    });
  }

  /**
   * Track guest service interactions
   */
  public trackGuestService(service: string, outcome: string, properties?: Record<string, any>): void {
    this.track('guest_service', {
      service,
      outcome,
      ...properties,
    });
  }

  /**
   * Track business metrics and KPIs
   */
  public trackBusinessMetric(metric: string, value: number, properties?: Record<string, any>): void {
    this.track('business_metric', {
      metric,
      value,
      ...properties,
    });
  }

  /**
   * Track user errors (complement to Sentry)
   */
  public trackUserError(component: string, userAction: string, severity: string, properties?: Record<string, any>): void {
    this.track('user_error', {
      component,
      userAction,
      severity,
      ...properties,
    });
  }

  /**
   * Reset analytics identity (on logout)
   */
  public reset(): void {
    if (!this.isEnabled) return;

    try {
      posthog.reset();
    } catch (error) {
      console.error('Analytics reset error:', error);
    }
  }

  /**
   * Opt-out of analytics capturing and persist preference
   */
  public optOut(): void {
    try {
      localStorage.setItem(AnalyticsManager.OPT_OUT_KEY, '1');
    } catch {
      /* no-op */
    }
    try {
      if (this.isInitialized) posthog.opt_out_capturing();
    } catch (error) {
      console.error('Analytics opt-out error:', error);
    }
  }

  /**
   * Opt-in to analytics capturing and persist preference removal
   */
  public optIn(): void {
    try {
      localStorage.removeItem(AnalyticsManager.OPT_OUT_KEY);
    } catch {
      /* no-op */
    }
    try {
      if (this.isInitialized) posthog.opt_in_capturing();
    } catch (error) {
      console.error('Analytics opt-in error:', error);
    }
  }

  /**
   * Check whether the current user has opted out
   */
  public isOptedOut(): boolean {
    try {
      return localStorage.getItem(AnalyticsManager.OPT_OUT_KEY) === '1';
    } catch {
      return false;
    }
  }

  /**
   * Scrub and validate properties to ensure privacy and data quality
   */
  private scrubProperties(properties?: Record<string, any>): Record<string, any> {
    if (!properties) return {};

    const allowedKeys = new Set([
      // Business context
      'hotelId', 'employeeId', 'platformUserId', 'role',
      // App/session context
      'environment', 'appVersion',
      // Event properties
      'feature', 'action', 'service', 'outcome', 'metric', 'value', 'unit', 'category',
      'subscription_method', 'unsubscribe_method',
      // Technical properties
      'duration_ms', 'count', 'status', 'requestId', 'component', 'userAction', 'severity',
      'processing_time_ms',
      // Content properties (safe)
      'query_length', 'result_count', 'fileType', 'fileSize', 'entityType',
      'priority', 'audience', 'message_length', 'field_count', 'success',
      'recipient_count', 'form_name',
      'request_type', 'new_status', 'from_status',
      // UI properties
      'buttonVariant', 'buttonSize', 'buttonText', 'path', 'title',
      // Profile/identity safe props
      'profile_type', 'viewed_user_id',
    ]);

    const scrubbed: Record<string, any> = {};

    for (const [key, value] of Object.entries(properties)) {
      // Only include allowed keys
      if (!allowedKeys.has(key)) {
        continue;
      }

      // Scrub and validate values
      if (value === null || value === undefined) {
        continue;
      }

      if (typeof value === 'string') {
        // Truncate long strings and check for potential PII
        const truncated = value.slice(0, 200);
        
        // Skip if it looks like an email or token
        if (truncated.includes('@') || truncated.includes('Bearer') || truncated.includes('token')) {
          continue;
        }
        
        scrubbed[key] = truncated;
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        scrubbed[key] = value;
      } else if (typeof value === 'object' && value !== null) {
        // Only allow simple objects, limit depth
        if (Array.isArray(value)) {
          scrubbed[key] = value.slice(0, 10); // Limit array size
        } else {
          // Flatten object to one level max
          const flatObject: Record<string, any> = {};
          for (const [subKey, subValue] of Object.entries(value)) {
            if (typeof subValue === 'string' || typeof subValue === 'number' || typeof subValue === 'boolean') {
              flatObject[subKey] = subValue;
            }
          }
          scrubbed[key] = flatObject;
        }
      }
    }

    // Check overall payload size
    const payloadString = JSON.stringify(scrubbed);
    if (payloadString.length > 8192) { // 8KB limit
      console.warn('Analytics payload too large, truncating');
      return { error: 'payload_too_large' };
    }

    return scrubbed;
  }

  /**
   * Get the current enabled status
   */
  public isAnalyticsEnabled(): boolean {
    return this.isEnabled;
  }
}

// Export singleton instance and convenience functions
export const analyticsManager = AnalyticsManager.getInstance();

// Main initialization function
export const initializeAnalytics = (config?: AnalyticsConfig) => analyticsManager.initialize(config);

// User context functions
export const identifyUser = (user: UserContext) => analyticsManager.identifyUser(user);
export const setAnalyticsContext = (properties: Record<string, string | number | boolean>) => 
  analyticsManager.setContext(properties);
export const resetAnalytics = () => analyticsManager.reset();

// Privacy controls
export const optOutAnalytics = () => analyticsManager.optOut();
export const optInAnalytics = () => analyticsManager.optIn();
export const isAnalyticsOptedOut = () => analyticsManager.isOptedOut();

// Event tracking functions
export const track = (eventName: string, properties?: Record<string, any>) => 
  analyticsManager.track(eventName, properties);
export const trackFeatureUsage = (feature: string, properties?: Record<string, any>) => 
  analyticsManager.trackFeatureUsage(feature, properties);
export const trackStaffAction = (action: string, properties?: Record<string, any>) => 
  analyticsManager.trackStaffAction(action, properties);
export const trackGuestService = (service: string, outcome: string, properties?: Record<string, any>) => 
  analyticsManager.trackGuestService(service, outcome, properties);
export const trackBusinessMetric = (metric: string, value: number, properties?: Record<string, any>) => 
  analyticsManager.trackBusinessMetric(metric, value, properties);
export const trackUserError = (component: string, userAction: string, severity: string, properties?: Record<string, any>) => 
  analyticsManager.trackUserError(component, userAction, severity, properties);

// Utility function to check if analytics is enabled
export const isAnalyticsEnabled = () => analyticsManager.isAnalyticsEnabled();
